import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { estimateApi } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { formatDate, formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { Search, FileText, Copy, AlertTriangle, Loader2, ArrowRight, Info, CheckCircle, Sparkles } from 'lucide-react';

/**
 * Dialog per clonare un preventivo esistente sul lead corrente (destination).
 *
 * Flusso:
 *   1. Step "search": input di ricerca + tabella risultati paginata.
 *   2. Step "confirm": riepilogo dati che verranno copiati e opzioni.
 *
 * Non espone l'opzione "copia allegati" — nel flusso cross-lead gli
 * allegati non vengono MAI copiati.
 */
const CloneEstimateFromExistingDialog = ({ destinationLeadId, destinationLeadName, open, onClose }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState('search'); // 'search' | 'confirm' | 'preview' | 'result'
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copyTerms, setCopyTerms] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // R.3 — Suggerimenti deterministici
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const idempotencyKeyRef = useRef(null);
  const PAGE_SIZE = 20;

  const search = useCallback(async (searchQ, pageNum) => {
    setLoading(true);
    try {
      const res = await estimateApi.searchReusableForLead(destinationLeadId, {
        q: searchQ || undefined,
        page: pageNum,
        page_size: PAGE_SIZE,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || pageNum);
    } catch (err) {
      toast.error('Errore durante la ricerca preventivi');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [destinationLeadId]);

  // Debounce ricerca su q
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(q, 1), 300);
    return () => clearTimeout(t);
  }, [q, open, search]);

  // Reset all'apertura
  useEffect(() => {
    if (open) {
      setStep('search');
      setQ('');
      setSelected(null);
      setCopyTerms(true);
      setCloneResult(null);
      setPreview(null);
      idempotencyKeyRef.current = null;
      // Carica suggerimenti automatici al primo mount
      setLoadingSuggestions(true);
      estimateApi.suggestionsForLead(destinationLeadId, { limit: 5, min_score: 0.20 })
        .then(res => setSuggestions(res.data.items || []))
        .catch(() => setSuggestions([]))
        .finally(() => setLoadingSuggestions(false));
    }
  }, [open, destinationLeadId]);

  const handleSelect = (item) => {
    setSelected(item);
    setStep('confirm');
  };

  const handleClone = async () => {
    if (!selected) return;

    // Step 1: se non ho ancora la preview, la carico e passo allo step 'preview'
    if (!preview) {
      setPreviewLoading(true);
      try {
        const res = await estimateApi.previewCloneFromExisting(destinationLeadId, {
          source_estimate_id: selected.id,
          copy_terms: copyTerms,
        });
        setPreview(res.data);
        setStep('preview');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Errore anteprima repricing');
      } finally {
        setPreviewLoading(false);
      }
      return;
    }

    // Step 2: preview già mostrata → esegui il clone reale
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = `clone-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    setCloning(true);
    try {
      const res = await estimateApi.cloneFromExisting(destinationLeadId, {
        source_estimate_id: selected.id,
        copy_terms: copyTerms,
        idempotency_key: idempotencyKeyRef.current,
      });
      const rs = res.data.repricing_summary || {};
      // Confronta con preview per detectare eventuali drift del listino
      const previewDelta = preview?.total_delta ?? 0;
      const actualDelta = rs.delta_total_amount ?? 0;
      const drift = Math.abs(previewDelta - actualDelta) > 0.01;

      setCloneResult({
        id: res.data.id,
        estimate_number: res.data.estimate_number,
        summary: rs,
        drift_vs_preview: drift ? { preview_delta: previewDelta, actual_delta: actualDelta } : null,
      });
      setStep('result');
      toast.success(`Preventivo creato: ${res.data.estimate_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella clonazione');
    } finally {
      setCloning(false);
    }
  };

  const goToEstimate = () => {
    if (!cloneResult) return;
    const id = cloneResult.id;
    onClose();
    navigate(`/estimates/${id}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="clone-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-[#FF7819]" />
            Usa un preventivo esistente
          </DialogTitle>
          <DialogDescription>
            {step === 'search' && `Cerca un preventivo da usare come base per ${destinationLeadName || 'questo lead'}. Verrà creata una nuova bozza indipendente con prezzi aggiornati al listino corrente.`}
            {step === 'confirm' && 'Rivedi le impostazioni. Al click su "Verifica prezzi" mostreremo l\'impatto del repricing prima della conferma.'}
            {step === 'preview' && 'Anteprima repricing: rivedi le variazioni di prezzo. La conferma userà il listino attivo al momento del click.'}
            {step === 'result' && 'Preventivo creato. Rivedi le variazioni di prezzo applicate.'}
          </DialogDescription>
        </DialogHeader>

        {/* ---------- STEP: SEARCH ---------- */}
        {step === 'search' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                <Input
                  data-testid="clone-search-input"
                  autoFocus
                  placeholder="Cerca per numero, cliente, città, categoria o descrizione lavorazione..."
                  className="pl-10"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <span className="text-xs text-[#737373] whitespace-nowrap">
                {loading ? '...' : `${total} risultat${total === 1 ? 'o' : 'i'}`}
              </span>
            </div>

            {/* R.3 — Suggerimenti automatici (mostrati solo se q è vuota) */}
            {!q && (loadingSuggestions || suggestions.length > 0) && (
              <div className="mb-3 border border-purple-200 bg-purple-50 rounded-md p-3" data-testid="clone-suggestions-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">Suggeriti per te</span>
                  <span className="text-xs text-purple-700">
                    (basati su tipologia, città e categorie)
                  </span>
                </div>
                {loadingSuggestions ? (
                  <div className="flex items-center gap-2 text-xs text-purple-700">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Calcolo suggerimenti...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s.estimate_id}
                        type="button"
                        onClick={() => handleSelect({
                          id: s.estimate_id,
                          estimate_number: s.estimate_number,
                          lead_display_name: s.lead_display_name,
                          city: s.city,
                          date: s.date,
                          total: s.total,
                          items_count: s.items_count,
                          categories: s.categories,
                        })}
                        data-testid={`clone-suggestion-${s.estimate_id}`}
                        className="text-left border border-purple-200 bg-white hover:border-purple-400 hover:bg-purple-50 rounded-md p-2 transition-colors"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-mono text-xs font-medium truncate">
                            {s.estimate_number}
                          </span>
                          <span className="text-xs font-semibold text-purple-700 whitespace-nowrap">
                            {Math.round(s.score * 100)}%
                          </span>
                        </div>
                        <p className="text-xs text-[#737373] mt-0.5 truncate">
                          {s.lead_display_name || '—'} · {formatCurrency(s.total)}
                        </p>
                        <ul className="text-[10px] text-purple-700 mt-1 space-y-0.5">
                          {(s.reasons || []).slice(0, 3).map((r, i) => (
                            <li key={i} className="truncate">✓ {r}</li>
                          ))}
                        </ul>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="border border-[#DCDCDC] rounded-md flex-1 overflow-auto min-h-[300px]">
              {loading && items.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#737373]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Caricamento...
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-[#737373]">
                  <FileText className="w-10 h-10 opacity-30 mb-2" />
                  <p className="text-sm">Nessun preventivo trovato</p>
                  {q && <p className="text-xs mt-1">Prova con parole chiave diverse</p>}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#F5F5F5] border-b border-[#DCDCDC]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-[#3C3C3C]">Numero</th>
                      <th className="text-left px-3 py-2 font-medium text-[#3C3C3C]">Cliente base</th>
                      <th className="text-left px-3 py-2 font-medium text-[#3C3C3C]">Città</th>
                      <th className="text-left px-3 py-2 font-medium text-[#3C3C3C]">Data</th>
                      <th className="text-left px-3 py-2 font-medium text-[#3C3C3C]">Cat.</th>
                      <th className="text-right px-3 py-2 font-medium text-[#3C3C3C]">Voci</th>
                      <th className="text-right px-3 py-2 font-medium text-[#3C3C3C]">Totale</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={it.id}
                        className="border-b border-[#EEEEEE] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                        onClick={() => handleSelect(it)}
                        data-testid={`clone-source-row-${it.id}`}
                      >
                        <td className="px-3 py-2 font-medium">{it.estimate_number}</td>
                        <td className="px-3 py-2">{it.lead_display_name || '—'}</td>
                        <td className="px-3 py-2 text-[#737373]">{it.city || '—'}</td>
                        <td className="px-3 py-2 text-[#737373]">{formatDate(it.date)}</td>
                        <td className="px-3 py-2 text-xs">
                          {(it.categories || []).slice(0, 3).join(', ')}
                          {(it.categories || []).length > 3 && '...'}
                        </td>
                        <td className="px-3 py-2 text-right">{it.items_count}</td>
                        <td className="px-3 py-2 text-right font-medium text-[#FF7819]">
                          {formatCurrency(it.total)}
                        </td>
                        <td className="px-3 py-2">
                          <ArrowRight className="w-4 h-4 text-[#737373]" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-xs text-[#737373]">
                <span>Pagina {page} di {totalPages}</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => search(q, page - 1)}
                  >
                    Precedente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => search(q, page + 1)}
                  >
                    Successiva
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ---------- STEP: CONFIRM ---------- */}
        {/* ---------- STEP: PREVIEW (anteprima repricing prima della conferma) ---------- */}
        {step === 'preview' && preview && (
          <div className="space-y-4" data-testid="clone-preview-panel">
            <div className="border border-blue-300 bg-blue-50 rounded-md p-4">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-sm text-blue-900 font-medium">
                    Anteprima variazione prezzi
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Fonte: {preview.source_estimate_number} · Listino: {preview.catalog_snapshot_at?.slice(0, 10)}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className={`text-lg font-semibold ${
                      preview.total_delta > 0 ? 'text-emerald-700'
                      : preview.total_delta < 0 ? 'text-blue-700'
                      : 'text-[#737373]'
                    }`}
                    data-testid="preview-total-delta"
                  >
                    {preview.total_delta > 0 ? '+' : ''}{formatCurrency(preview.total_delta)}
                  </span>
                  <p className="text-xs text-[#737373]">
                    {preview.total_delta_percent > 0 ? '+' : ''}{preview.total_delta_percent.toFixed(2)}%
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                <div className="bg-white rounded p-2">
                  <p className="text-[#737373]">Totale sorgente</p>
                  <p className="font-medium">{formatCurrency(preview.source_total)}</p>
                </div>
                <div className="bg-white rounded p-2">
                  <p className="text-[#737373]">Totale al listino attuale</p>
                  <p className="font-medium">{formatCurrency(preview.repriced_total)}</p>
                </div>
                <div className="bg-white rounded p-2">
                  <p className="text-[#737373]">Voci aggiornate</p>
                  <p className="font-medium">{preview.repriced_items_count}</p>
                </div>
                <div className="bg-white rounded p-2">
                  <p className="text-[#737373]">Voci fuori listino</p>
                  <p className="font-medium">{preview.missing_catalog_items_count}</p>
                </div>
              </div>
            </div>

            {preview.changed_items?.length > 0 && (
              <div className="border border-[#DCDCDC] rounded-md">
                <div className="px-3 py-2 bg-[#F5F5F5] border-b border-[#DCDCDC] text-sm font-medium">
                  Dettaglio variazioni ({preview.changed_items.length})
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-[#EEEEEE]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Codice</th>
                        <th className="text-left px-2 py-1.5">Descrizione</th>
                        <th className="text-right px-2 py-1.5">Sorgente</th>
                        <th className="text-right px-2 py-1.5">Corrente</th>
                        <th className="text-right px-2 py-1.5">Δ riga</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.changed_items.map((c) => (
                        <tr key={c.line_id} className="border-b border-[#F5F5F5]" data-testid={`preview-row-${c.line_id}`}>
                          <td className="px-2 py-1.5 font-mono text-[10px]">
                            {c.code || '—'}
                            {c.catalog_match_status === 'delisted' && (
                              <span className="ml-1 inline-block px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">
                                fuori listino
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 max-w-[220px] truncate" title={c.description}>
                            {c.description || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right text-[#737373]">{formatCurrency(c.source_unit_price)}</td>
                          <td className="px-2 py-1.5 text-right font-medium">{formatCurrency(c.current_unit_price)}</td>
                          <td className={`px-2 py-1.5 text-right ${
                            c.line_delta > 0 ? 'text-emerald-700'
                            : c.line_delta < 0 ? 'text-blue-700'
                            : 'text-[#737373]'
                          }`}>
                            {c.line_delta > 0 ? '+' : ''}{formatCurrency(c.line_delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="text-xs text-[#737373] p-2 rounded bg-[#F5F5F5]">
              <strong>Nota</strong>: il clone finale ricalcolerà nuovamente al listino attivo
              al momento della conferma. Se il listino cambia nel frattempo, il preventivo
              creato userà i prezzi più recenti; verrai avvisato se differiscono dall&apos;anteprima.
            </div>
          </div>
        )}

        {step === 'confirm' && selected && (
          <div className="space-y-4">
            <div className="border border-[#DCDCDC] rounded-md p-4 bg-[#FAFAFA]">
              <p className="text-sm text-[#737373] mb-2">Preventivo di base</p>
              <div className="flex items-baseline justify-between">
                <div>
                  <span className="font-medium text-[#3C3C3C]">{selected.estimate_number}</span>
                  <span className="text-xs text-[#737373] ml-2">
                    {selected.lead_display_name || 'Cliente sconosciuto'} · {formatDate(selected.date)}
                  </span>
                </div>
                <span className="font-medium text-[#FF7819]">{formatCurrency(selected.total)}</span>
              </div>
              <p className="text-xs text-[#737373] mt-2">
                {selected.items_count} voci · Categorie: {(selected.categories || []).join(', ') || '—'}
              </p>
            </div>

            <div className="border border-[#DCDCDC] rounded-md p-4">
              <p className="text-sm text-[#737373] mb-3">Impostazioni copia</p>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  id="copy-terms"
                  checked={copyTerms}
                  onCheckedChange={(v) => setCopyTerms(!!v)}
                  data-testid="clone-copy-terms-checkbox"
                />
                <div>
                  <Label htmlFor="copy-terms" className="cursor-pointer font-medium">
                    Copia note e condizioni tecniche
                  </Label>
                  <p className="text-xs text-[#737373] mt-0.5">
                    Copia note interne, condizioni di firma e note per categoria. Il nuovo preventivo
                    sarà marcato come <strong>da revisionare</strong> prima dell&apos;invio.
                  </p>
                </div>
              </label>
            </div>

            <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <p className="font-medium mb-1">Cosa NON viene copiato</p>
                <p>
                  Allegati, PDF, documenti, firme, contratti, pagamenti, SAL, storico email
                  e dati personali (cliente/indirizzo) restano legati al preventivo originale.
                  L&apos;anagrafica del nuovo preventivo sarà quella di <strong>{destinationLeadName}</strong>.
                </p>
              </div>
            </div>

            <div className="border border-blue-300 bg-blue-50 rounded-md p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-xs text-blue-900">
                <p className="font-medium mb-1">Prezzi allineati al listino corrente</p>
                <p>
                  Ogni voce collegata al listino verrà riportata al <strong>prezzo attuale</strong>.
                  Le voci fuori listino o custom manterranno il prezzo storico.
                  Vedrai un riepilogo delle variazioni al termine della creazione.
                </p>
              </div>
            </div>

            <div className="text-xs text-[#737373]">
              Il nuovo preventivo verrà creato in stato <strong>Bozza</strong> con un nuovo numero
              progressivo. Sarà tracciata la provenienza come <em>{`"Creato da ${selected.estimate_number}"`}</em>.
            </div>
          </div>
        )}

        {/* ---------- STEP: RESULT (alert post-clone con dettaglio repricing) ---------- */}
        {step === 'result' && cloneResult && (
          <div className="space-y-4" data-testid="clone-result-panel">
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-sm text-emerald-900 font-medium">
                    Preventivo creato: {cloneResult.estimate_number}
                  </p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Aggiornato al listino corrente
                  </p>
                </div>
                <span
                  className={`text-lg font-semibold ${
                    (cloneResult.summary.delta_total_amount || 0) >= 0
                      ? 'text-emerald-700' : 'text-blue-700'
                  }`}
                  data-testid="clone-result-delta-total"
                >
                  {(cloneResult.summary.delta_total_amount || 0) > 0 ? '+' : ''}
                  {formatCurrency(cloneResult.summary.delta_total_amount || 0)}
                </span>
              </div>
              <p className="text-xs text-emerald-800 mt-2">
                {cloneResult.summary.items_repriced} voc{cloneResult.summary.items_repriced === 1 ? 'e' : 'i'} con
                prezzo aggiornato · {cloneResult.summary.items_delisted_to_custom} voc{cloneResult.summary.items_delisted_to_custom === 1 ? 'e' : 'i'} fuori listino ora
                custom · {cloneResult.summary.items_kept_current} voc{cloneResult.summary.items_kept_current === 1 ? 'e' : 'i'} invariat{cloneResult.summary.items_kept_current === 1 ? 'a' : 'e'} · {cloneResult.summary.items_kept_custom} voc{cloneResult.summary.items_kept_custom === 1 ? 'e' : 'i'} custom
              </p>
            </div>

            {/* Dettaglio voci variate */}
            <div className="border border-[#DCDCDC] rounded-md">
              <div className="px-3 py-2 bg-[#F5F5F5] border-b border-[#DCDCDC] text-sm font-medium text-[#3C3C3C]">
                Dettaglio variazioni ({(cloneResult.summary.changed_items || []).length})
              </div>
              <div className="max-h-64 overflow-auto">
                {(cloneResult.summary.changed_items || []).length === 0 ? (
                  <p className="text-xs text-[#737373] p-3">Nessuna variazione.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-[#EEEEEE]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Codice</th>
                        <th className="text-left px-2 py-1.5">Descrizione</th>
                        <th className="text-right px-2 py-1.5">Prezzo sorgente</th>
                        <th className="text-right px-2 py-1.5">Prezzo applicato</th>
                        <th className="text-right px-2 py-1.5">Δ</th>
                        <th className="text-right px-2 py-1.5">Δ %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cloneResult.summary.changed_items || []).map((c) => (
                        <tr
                          key={c.line_id}
                          className="border-b border-[#F5F5F5]"
                          data-testid={`clone-result-row-${c.line_id}`}
                        >
                          <td className="px-2 py-1.5 font-mono text-[10px]">
                            {c.code || '—'}
                            {c.kind === 'delisted_to_custom' && (
                              <span className="ml-1 inline-block px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">
                                fuori listino
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 max-w-[200px] truncate" title={c.description}>
                            {c.description || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right text-[#737373]">
                            {formatCurrency(c.source_unit_price)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-medium">
                            {formatCurrency(c.applied_unit_price)}
                          </td>
                          <td className={`px-2 py-1.5 text-right ${
                            c.delta_amount > 0 ? 'text-emerald-700'
                            : c.delta_amount < 0 ? 'text-blue-700'
                            : 'text-[#737373]'
                          }`}>
                            {c.delta_amount > 0 ? '+' : ''}
                            {formatCurrency(c.delta_amount)}
                          </td>
                          <td className={`px-2 py-1.5 text-right ${
                            c.delta_percent > 0 ? 'text-emerald-700'
                            : c.delta_percent < 0 ? 'text-blue-700'
                            : 'text-[#737373]'
                          }`}>
                            {c.delta_percent != null
                              ? `${c.delta_percent > 0 ? '+' : ''}${c.delta_percent.toFixed(2)}%`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {cloneResult.summary.items_delisted_to_custom > 0 && (
              <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-900">
                  Alcune voci non sono più presenti nel listino corrente e sono state
                  convertite in <strong>voci custom</strong> con il prezzo storico.
                  Il preventivo è marcato <strong>da revisionare</strong>: rivedile prima dell&apos;invio.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          {step === 'search' && (
            <Button variant="outline" onClick={onClose} data-testid="clone-cancel-btn">
              Annulla
            </Button>
          )}
          {step === 'confirm' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('search')}
                disabled={previewLoading}
                data-testid="clone-back-btn"
              >
                Indietro
              </Button>
              <Button
                className="btn-primary"
                onClick={handleClone}
                disabled={previewLoading}
                data-testid="clone-confirm-btn"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifica prezzi...
                  </>
                ) : (
                  <>
                    <Info className="w-4 h-4 mr-2" />
                    Verifica prezzi
                  </>
                )}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button
                variant="outline"
                onClick={() => { setPreview(null); setStep('confirm'); }}
                disabled={cloning}
                data-testid="preview-back-btn"
              >
                Indietro
              </Button>
              <Button
                className="btn-primary"
                onClick={handleClone}
                disabled={cloning}
                data-testid="preview-confirm-clone-btn"
              >
                {cloning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Conferma e crea preventivo
                  </>
                )}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button
              className="btn-primary"
              onClick={goToEstimate}
              data-testid="clone-goto-estimate-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Vai al preventivo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloneEstimateFromExistingDialog;
